import { defineStore } from 'pinia'
import { APIS, LOADERS } from 'src/config/constants'
import { useLoader } from 'src/hooks/useLoader'
import type { IFamilyDashboard } from 'src/interfaces/client'
import type { IMessage } from 'src/interfaces/common'
import type { IFamily, IStudent } from 'src/interfaces/family'
import { useAggStore } from './agg.store'
import { useAppStore } from './app.store'
import { useAuthStore } from 'src/common/store'
import { Roles } from 'src/common/types'

export interface IPerState {
  selectedFamily: IFamily | null
  selectedStudent: IStudent | null
  familyDashboard: IFamilyDashboard | null
  medicationStudents: IStudent[] | null
  paymentSummaryPosition: string
  familyFilter: Record<string, string>
  adminUnreadMessages: Record<string, Record<string, number>> | null
  messagingSelectedMemberId: string | null
  messages: IMessage[] | null
}

export const usePerStore = defineStore('per', {
  state: (): IPerState => ({
    selectedFamily: null,
    selectedStudent: null,
    familyDashboard: null,
    medicationStudents: [],
    paymentSummaryPosition: 'absolute',
    familyFilter: {},
    adminUnreadMessages: null,
    messagingSelectedMemberId: null,
    messages: null,
  }),
  //this only used by client. should separate this store
  actions: {
    setMedicationStudents(students: IStudent[]) {
      this.medicationStudents = students
    },
    setSelectedFamily(family: IFamily) {
      this.selectedFamily = family
    },
    setSelectedStudent(student: IStudent) {
      this.selectedStudent = student
    },
    setFamilyDashboardFamily(family: IFamily) {
      if (this.familyDashboard) {
        this.familyDashboard.family = family
        return
      }
      this.familyDashboard = {
        family: family,
        studentRegistrationsMap: {},
      }
    },
    setPaymentSumaryPosition(position: string) {
      this.paymentSummaryPosition = position
    },
    setFamilyFilter(familyFilter: Record<string, string>) {
      this.familyFilter = familyFilter
    },
    setAdminUnreadMessages(
      adminUnreadMessages: Record<string, Record<string, number>> | null
    ) {
      this.adminUnreadMessages = adminUnreadMessages
    },
    setMessagingSelectedMemberId(memberId: string | null) {
      this.messagingSelectedMemberId = memberId
    },
    setMessages(messages: IMessage[]) {
      this.messages = messages
    },
    async refreshFamilies() {
      const authStore = useAuthStore()
      const appStore = useAppStore()
      const aggStore = useAggStore()
      if (authStore.role === Roles.Client) return
      const { fetchByPageLoader } = useLoader(LOADERS.agg, { uri: APIS.family })
      const offset = aggStore.familyNextOffset - appStore.tablePerPage
      await fetchByPageLoader({
        params: {
          limit: appStore.tablePerPage,
          offset: offset > 0 ? offset : 0,
          ...this.familyFilter,
        },
      }).then((result) => {
        aggStore.setFamiliesAndTotal({
          result: result.records,
          total: result.total,
          isReplace: true,
          nextOffset: aggStore.familyNextOffset,
        })
      })
    },
  },
})
